import React from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { useIntl } from 'react-intl'
import DatePickerISO from 'ui/components/DatePickerISO'
import YesNoSwitch from 'ui/components/YesNoSwitch'
import { TextField, SelectField } from 'react-md'
import { NumericTextField } from 'ui/components/NumericTextField'
import type { ReportFormHeader } from './types'

type FormInputProps = {
	input: ReportFormHeader
}

const ReportFormInput = ({ input }: FormInputProps) => {
	const {
		control,
		formState: { errors },
	} = useFormContext()
	const intl = useIntl()
	const title =
		typeof input.title === 'function' ? input.title(intl) : input.title
	const isRequired = !!input.required

	if (input.type === 'readonly') {
		return (
			<Controller
				name={input.key}
				control={control}
				render={({ field: { value, onChange, onBlur } }) => (
					<TextField
						id={input.key}
						label={title}
						lineDirection="center"
						floating
						fullWidth
						disabled={true}
						error={errors[input.key] !== undefined}
						type="text"
						value={value as string}
						onChange={onChange}
						required={isRequired}
						onBlur={onBlur}
					/>
				)}
			/>
		)
	} else if (input.type === 'string') {
		return (
			<Controller
				name={input.key}
				control={control}
				render={({ field: { value, onChange, onBlur } }) => (
					<TextField
						id={input.key}
						label={title}
						lineDirection="center"
						floating
						fullWidth
						disabled={false}
						error={errors[input.key] !== undefined}
						type="text"
						value={value as string}
						onChange={onChange}
						required={isRequired}
						onBlur={onBlur}
					/>
				)}
			/>
		)
	} else if (input.type === 'number') {
		return (
			<Controller
				name={input.key}
				control={control}
				render={({ field: { value, onChange, onBlur } }) => (
					<NumericTextField
						id={input.key}
						label={title}
						lineDirection="center"
						floating
						fullWidth
						error={errors[input.key] !== undefined}
						disabled={false}
						value={value as number}
						onChange={onChange}
						onBlur={onBlur}
						required={isRequired}
					/>
				)}
			/>
		)
	} else if (input.type === 'date') {
		return (
			<Controller
				name={input.key}
				control={control}
				render={({ field: { onChange, onBlur, value } }) => (
					<DatePickerISO
						style={{ width: '100%', paddingTop: '5px' }}
						id={input.key}
						error={errors[input.key] !== undefined}
						label={title}
						value={value || ''}
						disabled={false}
						onChange={(date) => {
							onChange(date)
							onBlur()
						}}
						required={isRequired}
					/>
				)}
			/>
		)
	} else if (input.type === 'boolean') {
		return (
			<Controller
				name={input.key}
				control={control}
				render={({ field: { onChange, onBlur, value } }) => (
					<YesNoSwitch
						label={isRequired ? `${title} *` : title}
						name={input.key}
						checked={!!value}
						disabled={false}
						onChange={(bool) => {
							onChange(bool)
							onBlur()
						}}
						style={{ marginTop: '10px' }}
					/>
				)}
			/>
		)
	} else if (input.type === 'select') {
		return (
			<Controller
				name={input.key}
				control={control}
				render={({ field: { onChange, onBlur, value } }) => (
					<SelectField
						id={input.key}
						label={title}
						style={{ width: '100%' }}
						//menuItems={field.menuItems ? field.menuItems(intl) : []}
						value={value?.toString()}
						onChange={(val) => {
							onChange(val)
							onBlur()
						}}
						required={isRequired}
					/>
				)}
			/>
		)
	}

	return null
}

export default ReportFormInput
